<template>
  <v-card flat class="weight-rep-card">
    <v-card flat class="input-card pa-4">
      <v-alert v-if="completedSets - warmupSetsCount >= (exercise.sets || []).length" type="info" dense text class="mb-4">
        Du hast alle Plan-Sätze abgeschlossen. Weitere Sätze werden mit deinen letzten Werten vorbelegt.
      </v-alert>

      <v-chip-group v-if="!isCompetition" v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="quick" outlined> ⚡ Schnellwahl </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <template v-if="mode === 'quick' || isCompetition">
        <template v-if="hasWeightSetting">
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="section-title white--text">Gewicht wählen</div>
            <v-btn v-if="allGlobalWeights.length > 6" x-small text color="primary" class="ma-0 pa-0" @click="showAllWeightsDialog = true">
              Alle Gewichte
            </v-btn>
          </div>
          <v-row dense no-gutters class="mb-4">
            <v-col cols="4" v-for="(preset, idx) in weightPresets" :key="`w-${idx}`" class="pa-1">
              <v-btn
                block
                outlined
                class="preset-btn"
                :class="{
                  'preset-selected-hyrox': isHyrox && weight === preset,
                  'preset-selected': !isHyrox && weight === preset,
                  'preset-selected--warmup': weight === preset && warmupActive,
                }"
                @click="setWeight(preset)">
                {{ preset }} kg
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <div class="section-title mb-2 white--text">Wiederholungen wählen</div>
        <v-row dense no-gutters class="mb-4">
          <v-col cols="4" v-for="(preset, idx) in repPresets" :key="`r-${idx}`" class="pa-1">
            <v-btn
              block
              outlined
              class="preset-btn"
              :class="{
                'preset-selected-hyrox': isHyrox && reps === preset,
                'preset-selected': !isHyrox && reps === preset,
                'preset-selected--warmup': reps === preset && warmupActive,
              }"
              @click="setReps(preset)">
              {{ preset }}
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-else-if="!isCompetition && mode === 'manual'">
        <v-row dense no-gutters class="mb-4 justify-space-between">
          <v-col v-if="hasWeightSetting" :cols="hasWeightSetting ? 6 : 12" class="pr-2">
            <v-text-field
              v-model.number="weight"
              label="Gewicht (kg)"
              type="number"
              inputmode="decimal"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-weight-lifter" />
          </v-col>
          <v-col :cols="hasWeightSetting ? 6 : 12" :class="{ 'pl-2': hasWeightSetting }">
            <v-text-field
              v-model.number="reps"
              inputmode="decimal"
              label="Wiederholungen"
              type="number"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-counter" />
          </v-col>
        </v-row>
      </template>

      <!-- Progressive Overload Coach -->
      <v-slide-y-transition>
        <div v-if="progressiveOverloadCoachEnabled && (lastWorkoutPerformance || overloadStatus)" class="overload-coach-card pa-3 mt-3 mb-1">
          <div class="d-flex align-center justify-space-between flex-wrap mb-1" style="gap: 4px">
            <span class="coach-label text-uppercase">Overload Coach</span>
            <v-slide-x-transition>
              <span v-if="overloadStatus" class="coach-status font-weight-black text-caption" :class="statusColorClass">
                {{ overloadStatus.text }}
              </span>
            </v-slide-x-transition>
          </div>
          <div v-if="lastWorkoutPerformance" class="coach-history text-caption white--text opacity-80">
            Letztes Mal: <span class="font-weight-bold">{{ lastWorkoutPerformance }}</span>
          </div>
        </div>
      </v-slide-y-transition>

      <div class="button-container">
        <v-btn v-if="warmupActive" text block @click="skip" class="skip-warmup-btn mb-3">
          <v-icon left>mdi-skip-next</v-icon>
          Warmup Überspringen
        </v-btn>
        <v-btn
          :color="warmupActive ? 'orange' : 'primary'"
          block
          class="add-set-btn elevation-2"
          :class="{ 'preset-selected--warmup': warmupActive, 'preset-selected': !warmupActive }"
          :disabled="!isValid"
          @click="submit">
          {{ warmupActive ? "Aufwärmsatz hinzufügen" : nextSetLabel }}
        </v-btn>
      </div>
    </v-card>

    <!-- Dialog für alle Gewichte -->
    <v-dialog v-model="showAllWeightsDialog" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
      <v-card class="glass-card pa-0" style="background: rgba(30, 30, 30, 0.98); backdrop-filter: blur(15px); border: none">
        <v-card-title
          class="headline write--text pb-2 pt-4 px-4 d-flex justify-space-between align-center"
          style="position: sticky; top: 0; z-index: 10; background: inherit">
          <div>
            <span
              style="
                font-size: 1.5rem;
                font-weight: 800;
                background: linear-gradient(90deg, #fff, #bbb);
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
              "
              >Community Gewichte</span
            >
          </div>
          <v-btn icon @click="showAllWeightsDialog = false" style="background: rgba(255, 255, 255, 0.1)">
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 px-4 pb-12">
          <v-alert
            dense
            outlined
            color="primary"
            class="mb-6"
            style="border-radius: 12px; font-size: 0.9rem; background: rgba(var(--v-primary-base-rgb), 0.05)">
            <div class="d-flex align-top">
              <v-icon color="primary" class="mr-3 mt-1">mdi-information-outline</v-icon>
              <span>Diese Werte basieren auf realen Trainingsdaten der Community für genau diese Übung.</span>
            </div>
          </v-alert>

          <v-row dense class="px-1">
            <v-col cols="4" sm="3" md="2" v-for="w in allGlobalWeights" :key="'all-w-' + w" class="pa-1">
              <v-btn
                block
                outlined
                class="preset-btn"
                style="min-height: 55px; font-size: 1.05rem"
                :class="{
                  'preset-selected-hyrox': isHyrox && weight === w,
                  'preset-selected': !isHyrox && weight === w,
                  'preset-selected--warmup': weight === w && warmupActive,
                }"
                @click="
                  setWeight(w)
                  showAllWeightsDialog = false
                ">
                {{ w }}<span style="font-size: 0.7em; margin-left: 2px">kg</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex"

export default {
  name: "WeightRepInput",
  props: ["exercise", "completedSets", "completedSetsArray", "warmup", "planSettings", "isCompetition", "isHyrox"],
  data() {
    return {
      mode: "quick",
      weight: null,
      reps: null,
      showAllWeightsDialog: false,
    }
  },
  computed: {
    warmupActive() {
      if (!this.warmup) return false
      // Aktiv, wenn noch gar keine Sätze gemacht wurden.
      if (this.completedSets === 0) return true
      // Aktiv, wenn der einzige Satz ein übersprungener Warmup-Satz ist.
      if (this.completedSets === 1 && this.completedSetsArray[0]?.skipped) {
        return true
      }
      return false
    },
    hasWeightSetting() {
      // 1. Prüfen, ob die Vorgaben für den aktuellen Satz ein Gewicht enthalten
      if (this.planSettings && this.planSettings.some((s) => s.text === "Gewicht")) {
        return true
      }

      // 2. Fallback für zusätzliche Sätze ohne Vorgabe: Hatte der letzte Satz ein Gewicht?
      if ((!this.planSettings || this.planSettings.length === 0) && this.completedSetsArray.length > 0) {
        const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
        return lastSet.some((e) => e.text === "Gewicht")
      }

      // 3. Genereller Fallback: Erlaubt die Übungsdefinition überhaupt ein Gewicht?
      if (this.exercise && this.exercise.set_exercise_settings) {
        return this.exercise.set_exercise_settings.some((s) => s.text === "Gewicht")
      }

      return false
    },
    // NEU: Ermittelt das Zielgewicht aus den Vorgaben oder dem letzten Satz.
    targetWeight() {
      if (!this.hasWeightSetting) return 0

      // 1. Suche das geplante Gewicht in den aktuellen Satzvorgaben
      const plannedWeightSetting = this.planSettings?.find((s) => s.text === "Gewicht")
      if (plannedWeightSetting && plannedWeightSetting.value) {
        return Number(plannedWeightSetting.value)
      }

      // 2. Fallback: Wenn keine Vorgabe da ist (z.B. extra Satz), nimm das Gewicht vom letzten Satz
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Gewicht")?.value) || 0
    },
    targetReps() {
      const getPlannedRep = (searchText) => this.planSettings?.find((s) => s.text === searchText)?.value

      // 1. Suche die geplanten Wiederholungen in den aktuellen Satzvorgaben
      const minRep = Number(getPlannedRep("Min Wiederholungen"))
      const maxRep = Number(getPlannedRep("Max Wiederholungen"))
      const singleRep = Number(getPlannedRep("Wiederholungen")) // Für Pläne mit nur einem Wdh-Wert

      if (minRep && maxRep) {
        return Math.round((minRep + maxRep) / 2) // Nimm den Mittelwert als Vorschlag
      }
      if (minRep || maxRep) {
        return minRep || maxRep
      }
      if (singleRep) {
        return singleRep
      }

      // 2. Fallback: Nimm die Wiederholungen vom letzten gemachten Satz
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => /Wiederholungen/.test(e.text))?.value) || 10
    },
    ...mapState("runWorkoutStore", ["globalWeightStats", "hubGroupedExercises", "progressiveOverloadCoachEnabled"]),
    allGlobalWeights() {
      if (!this.exercise) return []
      const exerciseId = this.exercise.exercise ? this.exercise.exercise._id : this.exercise._id
      return this.globalWeightStats[exerciseId] || []
    },
    weightPresets() {
      if (!this.hasWeightSetting) return []
      if (this.isCompetition) {
        return [this.targetWeight]
      }

      // Nutzen das aktuell ausgewählte Gewicht (falls vorhanden), ansonsten das Zielgewicht
      const center = Math.max(0, this.weight !== null ? this.weight : this.targetWeight)
      const round2 = (val) => Math.round(val * 100) / 100
      let presets = new Set()

      // Immer sicherstellen, dass das geplante (oder ausgewählte) Zielgewicht als Basis dabei ist
      presets.add(round2(center))

      // Hilfsfunktion zur Bestimmung der Schrittgrösse
      const getStepSize = (val) => {
        if (val <= 10) return 1
        if (val <= 20) return 2.5
        if (val > 100) return 10
        return 5
      }

      const stats =
        this.exercise && this.globalWeightStats
          ? this.globalWeightStats[this.exercise.exercise ? this.exercise.exercise._id : this.exercise._id] || []
          : []

      let currentUp = center
      let currentDown = center

      const getNextUp = (current) => {
        const step = getStepSize(current)
        const target = current + step
        // Gibt es einen globalen Wert in diesem Bereich? Nimm den kleinsten passenden.
        const communityMatch = stats.find((w) => w > current && w <= target)
        return communityMatch ? communityMatch : target
      }

      const getNextDown = (current) => {
        // Welcher Schrittwert gilt für den Bereich UNTER unserem aktuellen Wert?
        const step = getStepSize(Math.max(0, current - 0.1))
        const target = current - step
        if (target < 0) return null

        // Gibt es einen globalen Wert in diesem Bereich? Nimm den grössten passenden.
        const matches = stats.filter((w) => w < current && w >= target)
        return matches.length > 0 ? matches[matches.length - 1] : target
      }

      // Auffüllen auf 6 Presets (abwechselnd nach oben und unten interpolieren/extrapolieren)
      let iterations = 0
      while (presets.size < 6 && iterations < 20) {
        iterations++
        const oldSize = presets.size

        // Versuche einen Schritt nach oben
        currentUp = getNextUp(currentUp)
        presets.add(round2(currentUp))

        if (presets.size >= 6) break

        // Versuche einen Schritt nach unten
        const nextDown = getNextDown(currentDown)
        if (nextDown !== null) {
          currentDown = nextDown
          presets.add(round2(currentDown))
        } else {
          // Am Anschlag (0kg), fülle den Rest nach oben auf
          currentUp = getNextUp(currentUp)
          presets.add(round2(currentUp))
        }

        // Falls wir in einer Endlosschleife feststecken sollten
        if (presets.size === oldSize && nextDown === null) break
      }

      return Array.from(presets)
        .filter((p) => p >= 0)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    repPresets() {
      if (this.isCompetition) {
        return [this.targetReps]
      }
      const center = Math.max(1, this.targetReps)
      const presets = new Set()
      presets.add(center)
      presets.add(Math.max(1, center - 2))
      presets.add(Math.max(1, center - 1))
      presets.add(center + 1)
      presets.add(center + 2)
      presets.add(center + 4)
      return Array.from(presets)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },

    isValid() {
      if (this.hasWeightSetting) {
        // Wenn es eine Gewichtseinstellung gibt, muss Gewicht (kann 0 sein) und Reps > 0 gesetzt sein.
        return this.weight !== null && this.weight >= 0 && this.reps > 0
      } else {
        // Wenn es keine Gewichtseinstellung gibt, brauchen wir nur Reps > 0.
        return this.reps > 0
      }
    },

    // Bestehende Logik, die wir beibehalten:
    warmupActive() {
      return this.warmup && this.completedSets === 0
    },
    warmupSetsCount() {
      if (!this.warmup || this.completedSets === 0) return 0
      const warmupSet = this.completedSetsArray[0]
      return warmupSet && !warmupSet.skipped ? 1 : 0
    },
    realSetsCompleted() {
      return this.completedSetsArray.filter((set) => !set.skipped).length
    },
    nextSetLabel() {
      const nextNumber = this.realSetsCompleted - this.warmupSetsCount + 1
      return `${nextNumber}. Satz hinzufügen`
    },
    targetSlideIndex() {
      const totalSlides = (this.exercise?.sets?.length || 0) + (this.warmup ? 1 : 0)
      if (totalSlides === 0) return 0
      return Math.min(this.completedSets, totalSlides - 1)
    },
    lastWorkoutPerformance() {
      if (!this.exercise || !this.hubGroupedExercises) return null
      const exerciseId = this.exercise.exercise ? this.exercise.exercise._id : this.exercise._id
      const historyEntry = this.hubGroupedExercises.find((h) => {
        const hId = h.exercise && h.exercise._id ? h.exercise._id : h.exercise
        return hId === exerciseId
      })

      if (!historyEntry || !historyEntry.sets || historyEntry.sets.length === 0) return null

      // Finde das jüngste Datum
      const sets = [...historyEntry.sets]
      sets.sort((a, b) => new Date(a.date) - new Date(b.date))
      const lastSet = sets[sets.length - 1]

      if (!lastSet || !lastSet.steps || lastSet.steps.length === 0) return null

      // Formatiere die Sätze: "10 x 50kg, 8 x 55kg"
      return lastSet.steps
        .map((step) => {
          const reps = step.reps || 0
          const weight = step.weight !== undefined ? `${step.weight} kg` : ""
          return `${reps} Wdh` + (weight ? ` @ ${weight}` : "")
        })
        .join(" | ")
    },
    overloadStatus() {
      if (!this.weight || !this.reps) return null

      if (!this.exercise || !this.hubGroupedExercises) return null
      const exerciseId = this.exercise.exercise ? this.exercise.exercise._id : this.exercise._id
      const historyEntry = this.hubGroupedExercises.find((h) => {
        const hId = h.exercise && h.exercise._id ? h.exercise._id : h.exercise
        return hId === exerciseId
      })

      if (!historyEntry) return null

      // Max Weight und Max Volume aus der Historie
      const maxWeight = Number(historyEntry.summary?.maxWeightStep?.weight || 0)
      const maxVolume = Number((historyEntry.summary?.maxVolumeStep?.weight || 0) * (historyEntry.summary?.maxVolumeStep?.reps || 0))

      const currentWeight = Number(this.weight)
      const currentReps = Number(this.reps)
      const currentVolume = currentWeight * currentReps

      if (currentWeight > maxWeight) {
        return {
          type: "weight_record",
          text: "Neues Rekordgewicht! 🔥",
        }
      }

      if (currentVolume > maxVolume && currentWeight > 0) {
        return {
          type: "volume_record",
          text: "Neuer Satz-Volumenrekord! 🚀",
        }
      }

      // Prüfen, ob wir die letzte Session übertreffen
      const sets = [...historyEntry.sets]
      sets.sort((a, b) => new Date(a.date) - new Date(b.date))
      const lastSet = sets[sets.length - 1]
      if (lastSet && lastSet.steps && lastSet.steps.length > 0) {
        // Finde den entsprechenden Satz der letzten Session
        const currentSetIndex = this.completedSets
        const lastSessionSet = lastSet.steps[currentSetIndex] || lastSet.steps[lastSet.steps.length - 1]

        if (lastSessionSet) {
          const prevWeight = Number(lastSessionSet.weight || 0)
          const prevReps = Number(lastSessionSet.reps || 0)

          if (currentWeight > prevWeight || (currentWeight === prevWeight && currentReps > prevReps)) {
            return {
              type: "progression",
              text: "Steigerung zum letzten Mal! 💪",
            }
          }
        }
      }

      return null
    },
    statusColorClass() {
      if (!this.overloadStatus) return ""
      if (this.overloadStatus.type === "weight_record" || this.overloadStatus.type === "volume_record") {
        return "status-gold"
      }
      return "status-green"
    },
  },
  methods: {
    setWeight(val) {
      this.weight = val
    },
    setReps(val) {
      this.reps = val
    },

    // MODIFIZIERT: Sendet nur die relevanten Daten
    // In weightRepInput.vue -> methods
    submit() {
      if (!this.isValid) return
      const setPayload = []
      if (this.hasWeightSetting) {
        setPayload.push({ text: "Gewicht", value: this.weight })
      }
      setPayload.push({ text: "Wiederholungen", value: this.reps })

      // NEU: Markiere diesen Satz als Aufwärmsatz, wenn aktiv.
      if (this.warmupActive) {
        setPayload.isWarmup = true
      }

      this.$emit("add-set", setPayload)
      // Das Zurücksetzen der Werte nach dem Senden kann hier bleiben
      this.initializeValues()
    },

    skip() {
      this.$emit("add-set", [{ skipped: true, type: "warmup", id: Date.now() }])
    },

    // NEU: Initialisiert die Werte basierend auf den neuen computed properties
    initializeValues() {
      this.weight = this.targetWeight
      this.reps = this.targetReps
    },
  },
  watch: {
    // MODIFIZIERT: Beobachtet die planSettings und initialisiert die Werte neu.
    completedSets: {
      handler() {
        this.initializeValues()
      },
    },
    planSettings: {
      handler() {
        this.initializeValues()
      },
      immediate: true,
      deep: true,
    },
    targetSlideIndex: {
      handler(newIndex) {
        this.$emit("update-slide-index", newIndex)
      },
      immediate: true,
    },
  },
}
</script>
<style scoped>
.weight-rep-card {
  background: transparent !important;
}
.input-card {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
}
.timer-mode-chips .v-chip {
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
}
.timer-mode-chips .v-chip--active {
  border-color: var(--v-primary-base);
  background: rgba(var(--v-primary-base-rgb), 0.2);
}

.section-title {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 0.75rem;
  opacity: 0.7;
  margin-left: 4px;
}

.preset-btn {
  color: #fff;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  min-height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}
.preset-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.preset-selected {
  border: 1px solid #00963f !important;
  background: rgba(0, 150, 63, 0.15) !important;
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.4) !important;
  color: #00963f !important;
}
.preset-selected--warmup {
  background: rgba(255, 165, 0, 0.15) !important;
  border: 1px solid orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.3) !important;
  color: white !important;
}
.preset-selected-hyrox {
  background: rgba(255, 237, 0, 0.15) !important;
  border: 1px solid #ffed00 !important;
  box-shadow: 0 0 15px rgba(255, 237, 0, 0.3) !important;
  color: #ffed00 !important;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}
.add-set-btn {
  height: 56px !important;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: 100%;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, var(--v-primary-base), rgba(var(--v-primary-base-rgb), 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.add-set-btn.preset-selected--warmup {
  background: linear-gradient(135deg, #ff9800, #f57c00) !important;
}

.skip-warmup-btn {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.overload-coach-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.05);
  text-align: left;
}

.coach-label {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: var(--v-primary-base, #ffd700);
  opacity: 0.8;
}

.coach-history {
  font-size: 0.8rem !important;
  letter-spacing: 0.2px;
}

.coach-status {
  text-shadow: 0 0 8px currentColor;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
}

.status-green {
  color: #00e678 !important;
}

.status-gold {
  color: #ffd700 !important;
}

.opacity-80 {
  opacity: 0.8;
}
</style>
